import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import { Card, CardContent } from "@/components/ui/card";
import { Users2, Wallet, Store, Tag, ShieldAlert, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#047857", "#0284c7", "#d97706", "#7c3aed"];

export default function AdminDashboard() {
  const [s, setS] = useState(null);
  const [reports, setReports] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data));
    api.get("/admin/reports").then((r) => setReports(r.data));
  }, []);

  if (!s) return <div className="text-gray-500">Loading…</div>;

  const splitData = [
    { name: "Customer cashback", value: s.customer_cashback },
    { name: "Platform net", value: s.platform_net },
    { name: "Referral payouts", value: s.referral_payouts },
  ];

  return (
    <div data-testid="admin-dashboard">
      <PageHeader eyebrow="Admin" title="Platform overview" subtitle="Live revenue, cashback ledger, and growth metrics." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total users" value={s.users_total} icon={Users2} testId="adm-users" />
        <StatCard label="Active merchants" value={s.merchants_active} icon={Store} accent="sky" testId="adm-merchants" />
        <StatCard label="Active offers" value={s.offers_active} icon={Tag} accent="amber" testId="adm-offers" />
        <StatCard label="Pending payouts" value={s.pending_payouts} icon={Wallet} testId="adm-pending" />
        <StatCard label="GMV" value={fmtMoney(s.gmv)} icon={TrendingUp} accent="emerald" testId="adm-gmv" />
        <StatCard label="Gross commission" value={fmtMoney(s.gross_commission)} icon={Wallet} accent="emerald" testId="adm-commission" />
        <StatCard label="Customer cashback" value={fmtMoney(s.customer_cashback)} icon={Wallet} accent="sky" testId="adm-cb" />
        <StatCard label="Platform net" value={fmtMoney(s.platform_net)} icon={TrendingUp} accent="amber" testId="adm-net" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-gray-200 shadow-none rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg">Top merchants by GMV</h3>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={(reports?.top_merchants || []).slice(0, 8)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="merchant" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="gmv" fill="#047857" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-gray-200 shadow-none rounded-2xl">
          <CardContent className="p-6">
            <h3 className="font-display font-bold text-lg mb-4">Commission split</h3>
            {s.gross_commission > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={splitData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {splitData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                  </Pie>
                  <Legend verticalAlign="bottom" iconType="circle" />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-sm text-gray-500 py-12 text-center">No commission data yet. Will populate as users shop.</div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Card className="border-gray-200 shadow-none rounded-2xl">
          <CardContent className="p-0">
            <div className="p-5 border-b border-gray-200 font-display font-bold">Top earners</div>
            <ul className="divide-y divide-gray-100">
              {(reports?.top_users || []).slice(0, 6).map((u, i) => (
                <li key={i} className="px-5 py-3 flex items-center justify-between text-sm">
                  <span>{u.name}<span className="text-gray-400 ml-2">{u.email}</span></span>
                  <span className="font-semibold text-emerald-700">{fmtMoney(u.total_cashback)}</span>
                </li>
              ))}
              {(reports?.top_users || []).length === 0 && <li className="px-5 py-6 text-center text-gray-500 text-sm">No earners yet.</li>}
            </ul>
          </CardContent>
        </Card>

        <Card className="border-gray-200 shadow-none rounded-2xl">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3"><ShieldAlert className="w-4 h-4 text-rose-700" /><span className="font-display font-bold">Compliance health</span></div>
            <div className="text-sm space-y-2">
              <div className="flex justify-between"><span>Active fraud flags</span><span className="font-semibold">{s.fraud_flags}</span></div>
              <div className="flex justify-between"><span>Pending payout review</span><span className="font-semibold">{s.pending_payouts}</span></div>
              <div className="flex justify-between"><span>Customers</span><span className="font-semibold">{s.consumers}</span></div>
              <div className="flex justify-between"><span>Total purchases</span><span className="font-semibold">{s.purchases_total}</span></div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
